import { defaultRange } from "./DateRangePicker";

export type FilterSpec =
  | { kind: "select"; param: string }
  | { kind: "text"; param: string }
  | { kind: "numberRange"; minParam: string; maxParam: string }
  | { kind: "dateRange" };

export interface Column<T> {
  header: string;
  render: (row: T) => React.ReactNode;
  /** Server-side sort key. Omit and the header offers no sort entries. */
  sortKey?: string;
  /** Omit and the header menu offers no filter control. A column with
   *  neither sortKey nor filter renders as plain text with no menu at all. */
  filter?: FilterSpec;
  numeric?: boolean;
}

export interface DateRangeBinding {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

/** The `f_*` query params a spec owns. A dateRange owns none -- its value
 *  lives in page state and travels as `from`/`to`, not as an `f_` param. */
export function filterParams(spec: FilterSpec): string[] {
  if (spec.kind === "numberRange") return [spec.minParam, spec.maxParam];
  if (spec.kind === "dateRange") return [];
  return [spec.param];
}

/**
 * A date range is "filtered" only when it differs from the default window.
 * Every page seeds the last 30 days, so treating that as an active filter
 * would pin the chip bar open permanently and make "Clear all" look broken.
 */
export function isRangeNarrowed(range?: DateRangeBinding): boolean {
  if (!range) return false;
  const d = defaultRange();
  return range.from !== d.from || range.to !== d.to;
}

export function isColumnFiltered(
  spec: FilterSpec | undefined,
  filters: Record<string, string> | undefined,
  range?: DateRangeBinding,
): boolean {
  if (!spec) return false;
  if (spec.kind === "dateRange") return isRangeNarrowed(range);
  return filterParams(spec).some((p) => (filters?.[p] ?? "") !== "");
}

/** True when anything at all is narrowing the result set. */
export function hasAnyFilter<T>(
  columns: Column<T>[],
  filters: Record<string, string> | undefined,
  range?: DateRangeBinding,
): boolean {
  return columns.some((c) => isColumnFiltered(c.filter, filters, range));
}
